import { Box, Slab } from '../../render/parts'
import { BookStack } from '../parts/props'
import { BinderRow, Carcass, DoorFronts, Shelves } from '../parts/storage'
import type { AssetContext, AssetDefinition } from '../types'

const WIDTH = 1.2
const DEPTH = 0.45
const HEIGHT = 0.75
const WALL = 0.02
const PLINTH = 0.05

/** Front plane of the carcass — doors and pulls stack forward from here. */
const FRONT = DEPTH / 2
/** Clear opening between the bottom and top boards of the carcass. */
const OPEN_BOTTOM = PLINTH + WALL
const OPEN_TOP = HEIGHT - WALL
const OPEN_HEIGHT = OPEN_TOP - OPEN_BOTTOM
const MID = (OPEN_BOTTOM + OPEN_TOP) / 2
/** Door run is inset from the carcass sides so no two faces end up coplanar. */
const DOOR_WIDTH = WIDTH - 0.012

interface PullProps {
  x: number
  y: number
  z: number
  length: number
  accent?: string
}

/**
 * Slim vertical finger pull. The doors are pure white, so this is the one place
 * the accent lands on the closed variants.
 */
function Pull({ x, y, z, length, accent }: PullProps) {
  return (
    <Slab
      tone="accent"
      color={accent}
      size={[0.024, length, 0.014]}
      radius={0.006}
      rotation={[Math.PI / 2, 0, 0]}
      position={[x, y, z]}
    />
  )
}

/**
 * Low credenza, 1.2 x 0.45 x 0.75. Origin at the centre of the footprint,
 * y = 0 on the floor, doors facing +Z. The top is a usable surface, so printers
 * and plants stack onto it at 0.75.
 */
function CabinetLow({ accent, variant = 'sliding' }: AssetContext) {
  const open = variant === 'open'
  const sliding = variant === 'sliding'

  // Mirrors the leaf layout DoorFronts uses, so the pulls land on the leaves.
  const gap = sliding ? 0 : 0.006
  const leaf = (DOOR_WIDTH - gap * 3) / 2
  const doorZ = FRONT + (sliding ? 0.012 : 0.008)
  const leafCentre = (index: number) => -DOOR_WIDTH / 2 + gap + leaf / 2 + index * (leaf + gap)
  // Sliding leaves are pulled from their outer edges, hinged ones from the join.
  const pullX = (index: number) =>
    sliding
      ? leafCentre(index) + (index === 0 ? -1 : 1) * (leaf / 2 - 0.055)
      : leafCentre(index) + (index === 0 ? 1 : -1) * (leaf / 2 - 0.045)
  const pullZ = (index: number) => doorZ + (sliding && index === 1 ? 0.014 : 0) + 0.013

  return (
    <group>
      <Carcass width={WIDTH} depth={DEPTH} height={HEIGHT} plinth={PLINTH} wall={WALL} />

      {open ? (
        <group>
          {/* One mid shelf plus a central divider turns the box into four bays. */}
          <Shelves width={WIDTH} depth={DEPTH - 0.012} levels={[MID]} inset={0.016} />
          <Box
            tone="shell"
            size={[0.018, OPEN_HEIGHT + 0.02, DEPTH - 0.05]}
            position={[0, MID, -0.012]}
          />
          <group position={[-0.29, 0, -0.02]}>
            <BinderRow width={0.4} y={OPEN_BOTTOM - 0.002} height={0.27} accent={accent} seed={3} />
          </group>
          <group position={[0.3, 0, -0.02]}>
            <BookStack base={MID + 0.007} width={0.22} depth={0.17} accent={accent} seed={4} />
          </group>
        </group>
      ) : (
        <group>
          <DoorFronts
            width={DOOR_WIDTH}
            height={OPEN_HEIGHT}
            y={MID}
            depth={DEPTH}
            count={2}
            handle="none"
            sliding={sliding}
          />
          {[0, 1].map((index) => (
            <Pull
              key={index}
              x={pullX(index)}
              y={MID}
              z={pullZ(index)}
              length={0.34}
              accent={accent}
            />
          ))}
        </group>
      )}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'cabinet-low',
  name: 'Credenza baja',
  category: 'storage',
  footprint: [WIDTH, DEPTH],
  height: HEIGHT,
  surfaceHeight: HEIGHT,
  accentable: true,
  variants: [
    { id: 'sliding', name: 'Puertas correderas' },
    { id: 'hinged', name: 'Puertas batientes' },
    { id: 'open', name: 'Abierta' },
  ],
  keywords: [
    'credenza',
    'aparador',
    'mueble bajo',
    'armario bajo',
    'almacenaje',
    'sideboard',
    'low cabinet',
    'storage',
  ],
  Component: CabinetLow,
}
